import React, { useState } from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import BlockIcon from '@mui/icons-material/Block';
import {
    approveLibraryItem, newLibraryRevision, rejectLibraryItem, submitLibraryItem, withdrawLibraryItem
} from './apiLibrary';
import { BRAND } from './libraryConstants';

// Workflow buttons for one item, driven by item.permissions from the API.
// onChanged(updatedItem) after any state change; onEdit(item) opens the form.
const DIALOGS = {
    submit: {
        title: 'Submit for review',
        body: 'An approver will check this device before it is published. You can add a note for them.',
        confirm: 'Submit', noteLabel: 'Note for the reviewer (optional)', noteRequired: false
    },
    approve: {
        title: 'Approve & publish',
        body: 'Committee members will be able to find and download this device.',
        confirm: 'Approve & publish', noteLabel: 'Note (optional, included in the announcement)', noteRequired: false
    },
    reject: {
        title: 'Return to draft',
        body: 'The contributor will see your note and can fix the draft and submit it again.',
        confirm: 'Return to draft', noteLabel: 'What needs to change?', noteRequired: true
    },
    withdraw: {
        title: 'Withdraw device',
        body: 'The device is hidden from search and its files can no longer be downloaded. Direct links show it as withdrawn.',
        confirm: 'Withdraw', noteLabel: 'Reason', noteRequired: true
    }
};

const actionButtonSx = { textTransform: 'none' };

const ItemActions = ({ groupId, config, item, onChanged, onEdit, size = 'small' }) => {
    const p = item.permissions || {};
    const [dialog, setDialog] = useState(null); // { type, note, notify }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const open = (type) => {
        setError('');
        setDialog({ type, note: '', notify: config.settings.notifyOnPublish });
    };

    const run = async () => {
        const { type, note, notify } = dialog;
        if (DIALOGS[type].noteRequired && !note.trim()) { setError('Please add a note.'); return; }
        setBusy(true);
        const trimmed = note.trim() || undefined;
        const res = type === 'submit' ? await submitLibraryItem(groupId, item._id, trimmed)
            : type === 'approve' ? await approveLibraryItem(groupId, item._id, { notify, note: trimmed })
                : type === 'reject' ? await rejectLibraryItem(groupId, item._id, trimmed)
                    : await withdrawLibraryItem(groupId, item._id, trimmed);
        setBusy(false);
        if (res.error) { setError(res.error); return; }
        setDialog(null);
        onChanged(res);
    };

    const createRevision = async () => {
        setBusy(true);
        setError('');
        const res = await newLibraryRevision(groupId, item._id);
        setBusy(false);
        if (res.error) { setError(res.error); return; }
        onChanged(res);
        onEdit(res);
    };

    const showApprove = p.canApprove || p.approveBlockedReason;
    const anything = p.canEdit || p.canSubmit || showApprove || p.canReject || p.canCreateRevision || p.canWithdraw;
    if (!anything) return null;

    const spec = dialog && DIALOGS[dialog.type];

    return (
        <Box>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {p.canEdit && (
                    <Button size={size} variant="outlined" startIcon={<EditIcon />} sx={actionButtonSx} onClick={() => onEdit(item)}>Edit</Button>
                )}
                {p.canSubmit && (
                    <Button size={size} variant="contained" startIcon={<SendIcon />} onClick={() => open('submit')}
                        sx={{ ...actionButtonSx, bgcolor: BRAND.action, '&:hover': { bgcolor: BRAND.actionHover } }}>
                        Submit for review
                    </Button>
                )}
                {showApprove && (
                    <Tooltip title={p.approveBlockedReason || ''}>
                        {/* span so the tooltip still shows on a disabled button */}
                        <span>
                            <Button size={size} variant="contained" color="success" startIcon={<CheckCircleIcon />} sx={actionButtonSx}
                                disabled={!p.canApprove} onClick={() => open('approve')}>
                                Approve & publish
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {p.canReject && (
                    <Button size={size} variant="outlined" color="warning" startIcon={<UndoIcon />} sx={actionButtonSx} onClick={() => open('reject')}>Return to draft</Button>
                )}
                {p.canCreateRevision && (
                    <Button size={size} variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <ControlPointDuplicateIcon />} sx={actionButtonSx}
                        disabled={busy} onClick={createRevision}>
                        New revision
                    </Button>
                )}
                {p.canWithdraw && (
                    <Button size={size} variant="text" color="error" startIcon={<BlockIcon />} sx={actionButtonSx} onClick={() => open('withdraw')}>Withdraw</Button>
                )}
            </Stack>
            {!p.canApprove && p.approveBlockedReason && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{p.approveBlockedReason}</Typography>
            )}
            {error && !dialog && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}

            <Dialog open={Boolean(dialog)} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="sm">
                {spec && (
                    <>
                        <DialogTitle>{spec.title}: {item.code} r{item.revision}</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{spec.body}</Typography>
                            <TextField
                                autoFocus
                                fullWidth
                                multiline
                                minRows={3}
                                label={spec.noteLabel}
                                required={spec.noteRequired}
                                value={dialog.note}
                                onChange={e => setDialog(d => ({ ...d, note: e.target.value }))}
                                inputProps={{ maxLength: 2000 }}
                            />
                            {dialog.type === 'approve' && (
                                <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={<Checkbox checked={dialog.notify} onChange={e => setDialog(d => ({ ...d, notify: e.target.checked }))} />}
                                    label="Email committee members about this device"
                                />
                            )}
                            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
                            <Button variant="contained" onClick={run} disabled={busy}
                                color={dialog.type === 'withdraw' ? 'error' : dialog.type === 'approve' ? 'success' : 'primary'}
                                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
                                {spec.confirm}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default ItemActions;
